import React, { Component } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { forceCloseSession } from '../../../services/timeTrackingService';
import { convertFromUTC } from '../../../util/convert_timezone';

// Force close reason codes
const FORCE_CLOSE_REASONS = [
  { code: 'forgot_checkout', label: 'Forgot to check out' },
  { code: 'left_early', label: 'Left early' },
  { code: 'system_issue', label: 'System issue' },
  { code: 'manager_correction', label: 'Manager correction' },
  { code: 'other', label: 'Other' }
];

/**
 * ForceCloseModal - Confirmation modal for force closing an open session
 *
 * Props:
 * - show: Boolean - whether modal is visible
 * - onHide: Function - called when modal is closed
 * - onSuccess: Function - called after successful force close
 * - session: Object - the session being closed
 * - technicianName: String - name of the technician
 * - actorUserId: Number - the manager's user ID performing the action
 */
class ForceCloseModal extends Component {
  state = {
    reasonCode: 'forgot_checkout',
    note: '',
    loading: false,
    error: null
  };

  componentDidUpdate(prevProps) {
    // Reset state when modal opens
    if (this.props.show && !prevProps.show) {
      this.setState({
        reasonCode: 'forgot_checkout',
        note: '',
        loading: false,
        error: null
      });
    }
  }

  handleInputChange = (field, value) => {
    this.setState({ [field]: value, error: null });
  };

  handleSubmit = async () => {
    const { session, onSuccess, onHide, actorUserId } = this.props;
    const { reasonCode, note } = this.state;

    if (!reasonCode) {
      this.setState({ error: 'Please select a reason.' });
      return;
    }

    try {
      this.setState({ loading: true, error: null });

      await forceCloseSession(session.id, reasonCode, note, actorUserId);

      onHide();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Force close session error:', err);
      const errorMsg = err.response?.data?.message || 'Failed to close session. Please try again.';
      this.setState({ error: errorMsg, loading: false });
    }
  };

  // Format check-in time
  formatCheckInTime = () => {
    const { session } = this.props;
    if (!session?.check_in_at) return 'Unknown';
    return convertFromUTC(session.check_in_at).format('h:mm A');
  };

  // Get duration
  getDuration = () => {
    const { session } = this.props;
    if (!session?.check_in_at) return '';
    return convertFromUTC(session.check_in_at).fromNow(true);
  };

  render() {
    const { show, onHide, session, technicianName } = this.props;
    const { reasonCode, note, loading, error } = this.state;

    if (!session) return null;

    const name = technicianName || 'this technician';

    return (
      <Modal show={show} onHide={onHide} centered backdrop={loading ? 'static' : true}>
        <Modal.Header closeButton={!loading}>
          <Modal.Title>
            <i className="fa-sharp fa-regular fa-stop text-warning mr-2"></i>
            Force Close Session
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Confirmation Message */}
          <div className="alert alert-warning">
            <p className="mb-2">
              <strong>Force close this session for {name}?</strong>
            </p>
            <p className="mb-0 small">
              This will end their current shift. They have been checked in since{' '}
              <strong>{this.formatCheckInTime()}</strong> ({this.getDuration()}).
            </p>
          </div>

          {/* Reason Selection */}
          <div className="form-group">
            <label className="font-weight-bold">
              Reason <span className="text-danger">*</span>
            </label>
            <select
              className="form-control"
              value={reasonCode}
              onChange={(e) => this.handleInputChange('reasonCode', e.target.value)}
              disabled={loading}
            >
              {FORCE_CLOSE_REASONS.map(reason => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>

          {/* Note Field */}
          <div className="form-group mb-0">
            <label>Note (optional)</label>
            <textarea
              className="form-control"
              rows={2}
              value={note}
              onChange={(e) => this.handleInputChange('note', e.target.value)}
              placeholder="Add any additional details..."
              disabled={loading}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="alert alert-danger mt-3 mb-0">
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
              {error}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={this.handleSubmit}
            disabled={loading || !reasonCode}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2"></span>
                Closing...
              </>
            ) : (
              <>
                <i className="fa-sharp fa-regular fa-stop mr-2"></i>
                Force Close
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default ForceCloseModal;
